import { useState } from 'react'
import { useNavigation } from '@refinedev/core'
import { useLocation } from 'react-router'
import { RESOURCE_GROUPS } from '@/resources'
import { cn, toTitleCase } from '@/lib/utils'
import { getResourceIcon } from '@/components/layout/resourceIcons'
import {
  ChevronLeft, ChevronRight, LayoutDashboard, Building2, Key, SlidersHorizontal, X, Terminal, MessageCircle, Linkedin, GitBranch, UserCheck, Sprout, Plug,
} from 'lucide-react'
import fullLogo from '@/assets/logos/logo-landscape.png'
import miniLogo from '@/assets/logos/mini-logo.png'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { useGetIdentity } from '@refinedev/core'

const CUSTOM_LINKS: Array<{ label: string; path: string; icon: typeof Building2; beta?: boolean }> = [
  { label: 'Service Configuration',    path: '/custom/service-configuration',     icon: SlidersHorizontal },
  { label: 'Create Agency',            path: '/custom/create-agency',             icon: Building2 },
  { label: 'Create Greenhouse API Key', path: '/custom/create-greenhouse-api-key', icon: Key },
  { label: 'Partner API Clients',      path: '/custom/partner-clients',           icon: Plug },
  { label: 'Role Hierarchy',          path: '/custom/role-tree',                 icon: GitBranch },
  { label: 'Approval Chains',         path: '/custom/approval-chains',           icon: UserCheck, beta: true },
  { label: 'CLI Operations',           path: '/custom/cli-ops',                   icon: Terminal },
  { label: 'Messaging',                path: '/custom/messaging',                 icon: MessageCircle },
  { label: 'LinkedIn Monitoring',      path: '/custom/linkedin-monitoring',       icon: Linkedin },
  { label: 'Permission Seeder',         path: '/custom/permission-seed',          icon: Sprout, beta: true },
]

const BETA_RESOURCES = new Set(['account_feature_overrides'])

function isResourcePathActive(pathname: string, resourceName: string) {
  const base = `/${resourceName}`
  return pathname === base || pathname.startsWith(`${base}/`)
}

function isCustomPathActive(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(`${path}/`)
}

interface NavButtonProps {
  onClick: () => void
  active: boolean
  collapsed: boolean
  label: string
  icon: React.ReactNode
  beta?: boolean
}

function NavButton({ onClick, active, collapsed, label, icon, beta }: NavButtonProps) {
  const badge = beta ? (
    <span className="ml-auto shrink-0 rounded-full bg-[#076698]/10 dark:bg-[#076698]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#076698] dark:text-sky-300">
      BETA
    </span>
  ) : null
  const buttonClass = cn(
    'w-full flex items-center gap-3 text-sm font-medium transition-all duration-200 text-left',
    collapsed ? 'justify-center p-2.5 rounded-xl' : 'px-4 py-2.5 rounded-full',
    active
      ? 'bg-sky-100 text-[#076698] shadow-sm dark:bg-sky-900/50 dark:text-sky-200'
      : 'text-slate-500 dark:text-slate-400 hover:bg-white/40 dark:hover:bg-white/5 hover:text-slate-800 dark:hover:text-slate-200'
  )

  if (!collapsed) {
    return (
      <button type="button" onClick={onClick} className={buttonClass} aria-current={active ? 'page' : undefined} aria-label={label}>
        <span className={cn('shrink-0', active && 'text-[#076698] dark:text-sky-300')}>{icon}</span>
        {label}
        {badge}
      </button>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            onClick={onClick}
            className={buttonClass}
            aria-current={active ? 'page' : undefined}
            aria-label={label}
          >
            <span className={cn('shrink-0', active && 'text-[#076698] dark:text-sky-300')}>{icon}</span>
          </button>
        }
      />
      <TooltipContent side="right" sideOffset={8}>
        <p className="text-xs">{label}</p>
      </TooltipContent>
    </Tooltip>
  )
}

interface SidebarProps {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false)
  const { list, push } = useNavigation()
  const { pathname } = useLocation()
  const { data: identity } = useGetIdentity<{ username: string; email: string }>()

  const initials = identity?.username?.slice(0, 2).toUpperCase() ?? 'AD'

  const sidebarContent = (
    <TooltipProvider delay={300}>
      <aside className={cn(
        'relative flex flex-col h-full transition-all duration-300 overflow-y-auto overflow-x-hidden',
        'bg-white/55 dark:bg-slate-800/55 backdrop-blur-3xl',
        'border border-white/70 dark:border-white/10',
        'shadow-[0_20px_40px_rgba(7,102,152,0.06)] dark:shadow-[0_20px_40px_rgba(0,0,0,0.18)]',
        'rounded-3xl',
        collapsed ? 'w-16' : 'w-64'
      )}>
        <div className={cn(
          'flex items-center shrink-0 px-4 py-5',
          collapsed ? 'justify-center' : 'justify-between'
        )}>
          {!collapsed ? (
            <div className="flex flex-col gap-0.5 overflow-hidden">
              <img src={fullLogo} alt="Talentino" className="h-14 object-contain object-left shrink-0" />
              <span className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-bold">Admin Studio</span>
            </div>
          ) : (
            <img src={miniLogo} alt="Talentino" className="h-14 object-contain" />
          )}

          {!collapsed && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    onClick={() => setCollapsed(true)}
                    className="p-1.5 rounded-lg hover:bg-white/50 dark:hover:bg-white/10 shrink-0 transition-colors duration-150 text-slate-400"
                    aria-label="Collapse sidebar"
                  >
                    <ChevronLeft size={15} />
                  </button>
                }
              />
              <TooltipContent side="right" sideOffset={8}><p className="text-xs">Collapse</p></TooltipContent>
            </Tooltip>
          )}
        </div>

        {collapsed && (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  className="absolute -right-3.5 top-16 p-1 rounded-full bg-white dark:bg-slate-800 border border-border shadow-sm hover:bg-accent transition-colors duration-150 z-10"
                  aria-label="Expand sidebar"
                >
                  <ChevronRight size={13} />
                </button>
              }
            />
            <TooltipContent side="right" sideOffset={8}><p className="text-xs">Expand</p></TooltipContent>
          </Tooltip>
        )}

        <nav className="flex-1 py-2 space-y-0.5 px-3">
          <div className="mb-2">
            <NavButton
              onClick={() => push('/')}
              active={pathname === '/'}
              collapsed={collapsed}
              label="Dashboard"
              icon={<LayoutDashboard size={16} />}
            />
          </div>

          {Object.entries(RESOURCE_GROUPS).map(([group, tables]) => (
            <div key={group} className="mb-1">
              {!collapsed && (
                <p className="px-4 pt-4 pb-1 text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                  {group}
                </p>
              )}
              {collapsed && <div className="my-2 mx-1 h-px bg-border/50" />}
              <div className="space-y-0.5">
                {tables.map((name) => {
                  const Icon = getResourceIcon(name)
                  return (
                    <NavButton
                      key={name}
                      onClick={() => list(name)}
                      active={isResourcePathActive(pathname, name)}
                      collapsed={collapsed}
                      label={toTitleCase(name)}
                      icon={<Icon size={16} />}
                      beta={BETA_RESOURCES.has(name)}
                    />
                  )
                })}
              </div>
            </div>
          ))}

          <div className="mt-2 pt-2 border-t border-white/20 dark:border-white/10">
            {!collapsed && (
              <p className="px-4 pt-4 pb-1 text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                Operations
              </p>
            )}
            {collapsed && <div className="my-2 mx-1 h-px bg-border/50" />}
            <div className="space-y-0.5">
              {CUSTOM_LINKS.map(({ label, path, icon: CustomIcon, beta }) => (
                <NavButton
                  key={path}
                  onClick={() => push(path)}
                  active={isCustomPathActive(pathname, path)}
                  collapsed={collapsed}
                  label={label}
                  icon={<CustomIcon size={16} />}
                  beta={beta}
                />
              ))}
            </div>
          </div>
        </nav>

        {!collapsed && (
          <div className="shrink-0 px-4 pb-6 mt-4">
            <div className="flex items-center gap-3 px-2">
              <div className="w-9 h-9 rounded-full bg-[#076698] text-white flex items-center justify-center text-xs font-bold shrink-0">
                {initials}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{identity?.username ?? 'Admin'}</span>
                <span className="text-[10px] text-slate-400 truncate">{identity?.email ?? ''}</span>
              </div>
            </div>
          </div>
        )}
      </aside>
    </TooltipProvider>
  )

  return (
    <>
      <div className="hidden md:block shrink-0 h-full">
        {sidebarContent}
      </div>

      <div className={cn(
        'fixed inset-y-0 left-0 z-40 md:hidden transition-transform duration-200',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex flex-col h-full w-72 bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur-xl border-r border-white/20 shadow-2xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <img src={fullLogo} alt="Talentino" className="h-8 object-contain" />
            <button
              type="button"
              onClick={onMobileClose}
              className="p-1.5 rounded-lg hover:bg-white/40 text-slate-500"
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-3">
            <button
              type="button"
              onClick={() => { push('/'); onMobileClose?.() }}
              className={cn(
                'w-full flex items-center gap-3 rounded-full text-sm font-medium px-4 py-2.5 transition-all duration-200',
                pathname === '/'
                  ? 'bg-sky-100 text-[#076698] shadow-sm'
                  : 'text-slate-500 hover:bg-white/40 hover:text-slate-800'
              )}
            >
              <LayoutDashboard size={16} className="shrink-0" />
              Dashboard
            </button>

            {Object.entries(RESOURCE_GROUPS).map(([group, tables]) => (
              <div key={group}>
                <p className="px-4 pt-4 pb-1 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{group}</p>
                <div className="space-y-0.5">
                  {tables.map((name) => {
                    const Icon = getResourceIcon(name)
                    const active = isResourcePathActive(pathname, name)
                    const isBeta = BETA_RESOURCES.has(name)
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => { list(name); onMobileClose?.() }}
                        className={cn(
                          'w-full flex items-center gap-3 rounded-full text-sm font-medium px-4 py-2.5 transition-all duration-200 text-left',
                          active
                            ? 'bg-sky-100 text-[#076698] shadow-sm'
                            : 'text-slate-500 hover:bg-white/40 hover:text-slate-800'
                        )}
                      >
                        <Icon size={16} className="shrink-0" />
                        {toTitleCase(name)}
                        {isBeta && (
                          <span className="ml-auto shrink-0 rounded-full bg-[#076698]/10 dark:bg-[#076698]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#076698] dark:text-sky-300">BETA</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}

            <div className="mt-2 pt-2 border-t border-border/30">
              <p className="px-4 pt-4 pb-1 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Operations</p>
              <div className="space-y-0.5">
                {CUSTOM_LINKS.map(({ label, path, icon: CustomIcon, beta }) => {
                  const active = isCustomPathActive(pathname, path)
                  return (
                    <button
                      key={path}
                      type="button"
                      onClick={() => { push(path); onMobileClose?.() }}
                      className={cn(
                        'w-full flex items-center gap-3 rounded-full text-sm font-medium px-4 py-2.5 transition-all duration-200 text-left',
                        active
                          ? 'bg-sky-100 text-[#076698] shadow-sm'
                          : 'text-slate-500 hover:bg-white/40 hover:text-slate-800'
                      )}
                    >
                      <CustomIcon size={16} className="shrink-0" />
                      {label}
                      {beta && (
                        <span className="ml-auto shrink-0 rounded-full bg-[#076698]/10 dark:bg-[#076698]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#076698] dark:text-sky-300">BETA</span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}
